import { MergeFormConfig, FieldConfig } from "@/types/merge";
import { JobData } from "@/hooks/jobs/useJobsData";

export const jobMergeConfig: MergeFormConfig<JobData> = {
	entityName: "jobs",
	excludeFields: ["id", "company", "matchCount", "createdAt", "embedding", "embedding_specific", "organization_uuid", "company_id", "contact_id", "job_fts"],
	fields: [
		{
			name: "title",
			type: "text",
			label: "Job Title",
			mergeBehavior: "manual",
			required: true,
			description: "Job position title"
		},
		{
			name: "description",
			type: "text",
			label: "Description",
			mergeBehavior: "ai",
			aiMergeEnabled: true,
			description: "Job description and requirements"
		},
		{
			name: "ai_summary",
			type: "text",
			label: "AI Summary",
			mergeBehavior: "ai",
			aiMergeEnabled: true,
			description: "AI-generated job summary"
		},
		{
			name: "ai_analysis",
			type: "presence",
			label: "AI Analysis",
			mergeBehavior: "combine",
			description: "Choose which AI analysis to keep (complex data structure)"
		},
		{
			name: "skills",
			type: "array",
			label: "Skills",
			mergeBehavior: "combine",
			description: "Required skills"
		},
		{
			name: "hard_skills",
			type: "array",
			label: "Hard Skills",
			mergeBehavior: "combine",
			description: "Technical hard skills required"
		},
		{
			name: "soft_skills",
			type: "array",
			label: "Soft Skills",
			mergeBehavior: "combine",
			description: "Soft skills and interpersonal abilities"
		},
		{
			name: "culture_tags",
			type: "array",
			label: "Culture Tags",
			mergeBehavior: "combine",
			description: "Company culture and work environment tags"
		},
		{
			name: "tags",
			type: "array",
			label: "Tags",
			mergeBehavior: "combine",
			description: "General job tags and categories"
		},
		{
			name: "employment_type",
			type: "text",
			label: "Employment Type",
			mergeBehavior: "manual",
			description: "Type of employment (full-time, part-time, contract, etc.)"
		},
		{
			name: "employment_classification",
			type: "text",
			label: "Employment Classification",
			mergeBehavior: "manual",
			description: "Employment classification details"
		},
		{
			name: "time_commitment",
			type: "text",
			label: "Time Commitment",
			mergeBehavior: "manual",
			description: "Required time commitment"
		},
		{
			name: "contract_duration",
			type: "text",
			label: "Contract Duration",
			mergeBehavior: "manual",
			description: "Duration of contract if applicable"
		},
		{
			name: "seniority_level",
			type: "text",
			label: "Seniority Level",
			mergeBehavior: "manual",
			description: "Required experience/seniority level"
		},
		{
			name: "education_level",
			type: "text",
			label: "Education Level",
			mergeBehavior: "manual",
			description: "Required education level"
		},
		{
			name: "years_of_experience",
			type: "text",
			label: "Years of Experience",
			mergeBehavior: "manual",
			description: "Required years of experience"
		},
		{
			name: "language_requirements",
			type: "text",
			label: "Language Requirements",
			mergeBehavior: "manual",
			description: "Required languages for the position"
		},
		{
			name: "extra_lang",
			type: "text",
			label: "Additional Languages",
			mergeBehavior: "manual",
			description: "Additional language preferences"
		},
		{
			name: "budget",
			type: "text",
			label: "Budget",
			mergeBehavior: "manual",
			description: "Salary or budget for permanent positions"
		},
		{
			name: "budget_freelance",
			type: "text",
			label: "Freelance Budget",
			mergeBehavior: "manual",
			description: "Budget for freelance/contract positions"
		},
		{
			name: "location_type",
			type: "text",
			label: "Location Type",
			mergeBehavior: "manual",
			description: "Type of work location (remote, on-site, hybrid)"
		},
		{
			name: "workplace_breakdown",
			type: "text",
			label: "Workplace Breakdown",
			mergeBehavior: "manual",
			description: "Detailed workplace arrangement breakdown"
		},
		{
			name: "start_date",
			type: "date",
			label: "Start Date",
			mergeBehavior: "latest",
			description: "Preferred start date"
		},
		{
			name: "end_date",
			type: "date",
			label: "End Date",
			mergeBehavior: "latest",
			description: "Expected end date"
		},
		{
			name: "application_deadline",
			type: "date",
			label: "Application Deadline",
			mergeBehavior: "latest",
			description: "Deadline for applications"
		},
		{
			name: "job_posting_date",
			type: "date",
			label: "Job Posting Date",
			mergeBehavior: "latest",
			description: "Date when job was originally posted"
		},
		{
			name: "source",
			type: "text",
			label: "Source",
			mergeBehavior: "manual",
			description: "Source where the job was found or posted"
		}
	],
	customMergeLogic: async (merged: Partial<JobData>, sources: JobData[]): Promise<Partial<JobData>> => {
		return merged;
	}
};
